import EarthFilled16 from '@carbon/icons-react/es/earth--filled/16';
import capitalize from 'lodash/capitalize';
import keyBy from 'lodash/keyBy';
import moment, { ISO_8601 } from 'moment';
import React, { FC, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { TemplateId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { RelativeTime } from 'components/atoms/relative-time';
import { defaultFilter } from 'components/molecules/filters/utils';
import { Page } from 'components/molecules/page';
import { DocumentProvider } from 'components/organisms/document/document-store';
import { FilterableField } from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { TemplateUnsavedPrompt } from 'components/organisms/templates/commons/prompt';
import {
  getApiFilterTransformer,
  to_filter,
} from 'components/pages/api-filter-transformer';
import { RoutePaths } from 'constant';
import { setDirtyState } from 'data/templates';
import {
  TemplateIdType,
  TemplateObjectType,
  TemplateState,
} from 'data/templates/api';
import { selectDirtyState } from 'data/templates/selectors';
import {
  Frequency,
  ProjectTargets,
  StatusUpdateTemplateDetails as StatusUpdateTemplateType,
} from 'data/templates/status-updates/type';
import { useUpdateStatusUpdateTemplateMutation } from 'queries/templates/status-update-templates';
import { fullName } from 'utils/full-name';
import { getTemplateUrl } from 'utils/urls';
import { TemplateBreadcrump } from '../commons';
import { StatusUpdatesTemplatesContext } from './context';
import { useSuTemplateProjectFilterFields } from './filter-config';
import { StatusUpdateTemplate } from './status-update-template-wrapper';
import { Actions, Header, HeaderWrapper, LastSavedText } from './styles';

const Content = styled(Page.Content)`
  height: calc(100% - 85px);
`;

const StatusUpdatesTemplateDetailsComp: FC<{
  template: StatusUpdateTemplateType;
  fields: FilterableField[];
  serverNextPublishAt?: string | null;
}> = ({ template, fields, serverNextPublishAt }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { templateId, templateContent, templateMeta } = template;

  const [editorContent, setEditorContent] = useState<string>(
    templateContent.content,
  );
  const [serverPublishedTime, setServerPublishedTime] = useState<string | null>(
    serverNextPublishAt ?? null,
  );

  const [templateName, setTemplateName] = useState<string>(
    template.templateName ?? '',
  );

  const transformer = getApiFilterTransformer(keyBy(fields, 'fieldName'));
  const filters = templateContent.filters
    ? transformer.from(templateContent.filters)
    : null;
  const [projectFilter, setProjectFilter] = useState(filters || defaultFilter);
  const dirty = useSelector(selectDirtyState(templateId));

  const [isPublished, setPublished] = useState<boolean>(
    templateContent.enableAutoPublish,
  );
  const [isPrivate, setPrivate] = useState<boolean>(templateContent.private);
  const projectTargetType = templateContent.filters?.match
    ? ProjectTargets.Filtered
    : ProjectTargets.All;

  const [projectTarget, onProjectTargetChange] =
    useState<ProjectTargets>(projectTargetType);

  const interval =
    templateContent.criteria[templateContent.criteria.timeFrame?.toLowerCase()]
      .frequency;

  const initialFrequency = {
    type: {
      label: capitalize(templateContent.criteria.timeFrame),
      value: templateContent.criteria.timeFrame,
    },
    interval: {
      label: interval,
      value: interval,
    },
    onDay: {
      label: templateContent.criteria.week.days[0],
      value: templateContent.criteria.week.days[0],
    },
    onMonthDay: {
      label: templateContent.criteria.month.day,
      value: template.templateContent.criteria.month.day,
    },
    onTime: {
      label: template.templateContent.criteria.time,
      value: template.templateContent.criteria.time,
    },
  };

  const [frequency, updateFrequency] = useState<Frequency>(initialFrequency);
  const {
    mutate: updateStatusTemplateMuatate,
    isLoading: isUpdateStatusTemplateCreationLoading,
  } = useUpdateStatusUpdateTemplateMutation(templateId as number);

  const onSaveTemplate = () => {
    const filter =
      projectTarget !== ProjectTargets.All &&
      projectFilter.criteria.filter(FieldFilter.isValid).length
        ? to_filter(projectFilter)
        : null;

    const statusUpdatePayload = {
      templateId,
      templateName,
      templateMeta,
      templateContent: {
        statusUpdateTitle: templateName,
        content: editorContent,
        criteria: {
          time: frequency.onTime.value,
          week: {
            days: [frequency.onDay.value.toUpperCase()],
            frequency: frequency.interval.value,
          },
          month: {
            day: frequency.onMonthDay.value,
            frequency: frequency.interval.value,
          },
          timeFrame: frequency.type.value,
        },
        private: isPrivate,
        enableAutoPublish: isPublished,
        filters: filter,
      },
      objectType: TemplateObjectType.StatusUpdates,
      state: TemplateState.Published,
    };

    updateStatusTemplateMuatate(
      statusUpdatePayload as StatusUpdateTemplateType,
      {
        onSuccess: (newTemplate) => {
          if (template.templateId === TemplateIdType.New) {
            history.replace(
              getTemplateUrl(
                TemplateId(newTemplate.templateId as number),
                TemplateObjectType.StatusUpdates,
              ),
            );
          }
          dispatch(
            setDirtyState({
              id: template.templateId,
              _state: false,
            }),
          );
        },
      },
    );
  };

  const contextValues = {
    templateName: template.templateName,
    templateId: template.templateId as number,
    editorContent,
    serverPublishedTime,
    isPublished,
    isPrivate,
    projectTarget,
    projectFilter,
    frequency,
    setServerPublishedTime,
    setEditorContent,
    setTemplateName,
    setPublished,
    setProjectFilter,
    setPrivate,
    onProjectTargetChange,
    updateFrequency,
  };

  return (
    <StatusUpdatesTemplatesContext.Provider value={contextValues}>
      <Page>
        <Helmet>
          <title>{`${templateName}`}</title>
        </Helmet>
        <TemplateUnsavedPrompt />
        <Page.Header
          breadcrumbs={
            <TemplateBreadcrump
              route={RoutePaths.statusUpdateTemplateRoot}
              id="templates.status_updates"
            />
          }
          title={
            <HeaderWrapper>
              <Header>{templateName}</Header>
              <Actions>
                {template?.publishedAt && (
                  <LastSavedText data-cy="templates.last_saved_at">
                    <FormattedMessage
                      id="templates.last_saved_at"
                      values={{
                        by:
                          template?.publishedBy &&
                          fullName(template?.publishedBy),
                        time: (
                          <RelativeTime
                            time={moment(template?.publishedAt, ISO_8601)}
                          />
                        ),
                      }}
                    />
                  </LastSavedText>
                )}
                <Button
                  data-cy="templates.save"
                  type="primary"
                  disabled={!dirty?.state}
                  iconPlacement="right"
                  icon={<EarthFilled16 />}
                  loading={isUpdateStatusTemplateCreationLoading}
                  onClick={onSaveTemplate}
                >
                  <FormattedMessage id="save" />
                </Button>
              </Actions>
            </HeaderWrapper>
          }
        />
        <Content>
          <DocumentProvider>
            <StatusUpdateTemplate />
          </DocumentProvider>
        </Content>
      </Page>
    </StatusUpdatesTemplatesContext.Provider>
  );
};

export const StatusUpdatesTemplateDetails: FC<{
  template: StatusUpdateTemplateType;
  serverNextPublishAt?: string | null;
}> = ({ template, serverNextPublishAt }) => {
  const [fields, isFieldsLoading] = useSuTemplateProjectFilterFields();

  return isFieldsLoading ? (
    <Loading />
  ) : (
    <StatusUpdatesTemplateDetailsComp
      template={template}
      fields={fields}
      serverNextPublishAt={serverNextPublishAt}
    />
  );
};
